import { useMemo, useRef, type ReactNode } from 'react';
import { Badge, Empty, List, Tag } from '@viquard/doc-ui/components';
import { useReducedMotion } from '@viquard/doc-ui/hooks';
import type {
  CalendarEvent,
  CalendarEventLink,
  CalendarGroup,
  CalendarKindMap,
} from '../types.js';
import {
  addDays,
  compareDay,
  formatDayShort,
  isoDate,
  localDateKey,
  localTime,
  parseISODate,
  startOfWeek,
  weekRangeLabel,
} from '../utils/dateUtils.js';
import { RailCard } from '../components/RailCard.js';
import { EventCard } from '../components/EventCard.js';
import type { EventMenuActions } from '../utils/eventMenu.js';
import { eventInstanceKey } from '../utils/identity.js';
import {
  formatEventCount,
  relativeDayLabelLocalized,
  useCalendarMessages,
} from '../i18n.js';

export interface AgendaViewProps {
  readonly events: readonly CalendarEvent[];
  readonly today: string;
  readonly kindTheme: CalendarKindMap;
  readonly groups?: readonly CalendarGroup[] | undefined;
  readonly locale?: string | undefined;
  readonly viewerTz?: string | undefined;
  readonly onSelectEvent?: ((event: CalendarEvent) => void) | undefined;
  readonly onSelectLink?:
    | ((link: CalendarEventLink, event: CalendarEvent) => void)
    | undefined;
  readonly weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  readonly renderEventExtras?: ((event: CalendarEvent) => ReactNode) | undefined;
  readonly contextMenu?: ((event: CalendarEvent) => EventMenuActions) | undefined;
  readonly selectedEventKey?: string | null | undefined;
}

interface AgendaWeek {
  readonly key: string;
  readonly start: ReturnType<typeof parseISODate>;
  readonly end: ReturnType<typeof parseISODate>;
  readonly days: readonly { readonly date: string; readonly list: readonly CalendarEvent[] }[];
}

export function AgendaView(props: AgendaViewProps): ReactNode {
  const messages = useCalendarMessages();
  const reducedMotion = useReducedMotion();
  const {
    events,
    today,
    kindTheme,
    groups,
    locale,
    viewerTz,
    onSelectEvent,
    onSelectLink,
    weekStartsOn = 0,
    renderEventExtras,
    contextMenu,
    selectedEventKey,
  } = props;
  const todayDate = useMemo(() => parseISODate(today), [today]);

  const weekRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  const sorted = useMemo(
    () => events.slice().sort((a, b) => a.startsAt.localeCompare(b.startsAt)),
    [events],
  );

  const weeks = useMemo<AgendaWeek[]>(() => {
    const map = new Map<string, { start: ReturnType<typeof parseISODate>; days: Map<string, CalendarEvent[]> }>();
    sorted.forEach((e) => {
      const dayKey = localDateKey(e.startsAt, viewerTz);
      const date = parseISODate(dayKey);
      const wkStart = startOfWeek(date, weekStartsOn);
      const wkKey = isoDate(wkStart);
      let bucket = map.get(wkKey);
      if (bucket === undefined) {
        bucket = { start: wkStart, days: new Map() };
        map.set(wkKey, bucket);
      }
      const list = bucket.days.get(dayKey);
      if (list === undefined) bucket.days.set(dayKey, [e]);
      else list.push(e);
    });
    return Array.from(map.entries()).map(([key, val]) => ({
      key,
      start: val.start,
      end: addDays(val.start, 6),
      days: Array.from(val.days.entries())
        .map(([date, list]) => ({ date, list }))
        .sort((a, b) => a.date.localeCompare(b.date)),
    }));
  }, [sorted, weekStartsOn, viewerTz]);

  const upcomingCount = sorted.filter(
    (e) => compareDay(parseISODate(localDateKey(e.startsAt, viewerTz)), todayDate) >= 0,
  ).length;
  const pastCount = sorted.length - upcomingCount;
  const nextUp = sorted.find(
    (e) => compareDay(parseISODate(localDateKey(e.startsAt, viewerTz)), todayDate) >= 0,
  );

  const byKind = useMemo(() => {
    const m = new Map<string, number>();
    sorted.forEach((e) => m.set(e.kind, (m.get(e.kind) ?? 0) + 1));
    return m;
  }, [sorted]);

  return (
    <div className="vq-cal__split vq-cal__split--agenda">
      <div className="vq-cal__agenda-stack">
        {weeks.length === 0 && (
          <div className="vq-cal__agenda-empty">
            <Empty
              title={messages.nothingScheduled}
              description={messages.nothingScheduledHint}
            />
          </div>
        )}
        {weeks.map((wk) => {
          const isCurrent =
            compareDay(wk.start, todayDate) <= 0 && compareDay(todayDate, wk.end) <= 0;
          const total = wk.days.reduce((n, d) => n + d.list.length, 0);
          return (
            <section
              key={wk.key}
              className="vq-cal__agenda-week"
              ref={(el) => {
                weekRefs.current.set(wk.key, el);
              }}
            >
              <header className="vq-cal__agenda-week-head">
                <div className="vq-cal__agenda-week-meta">
                  <span className="vq-cal__agenda-week-eyebrow">{messages.agendaWeekOf}</span>
                  <span className="vq-cal__agenda-week-range">
                    {weekRangeLabel(wk.start, wk.end, locale)}
                  </span>
                  {isCurrent && (
                    <Tag tone="accent" size="sm">
                      {messages.thisWeek}
                    </Tag>
                  )}
                </div>
                <Badge
                  tone="neutral"
                  size="md"
                  numeric
                  label={formatEventCount(messages, total)}
                >
                  {total}
                </Badge>
              </header>
              <div className="vq-cal__agenda-days">
                {wk.days.map(({ date, list }) => {
                  const d = parseISODate(date);
                  const isToday = compareDay(d, todayDate) === 0;
                  const isPast = compareDay(d, todayDate) < 0;
                  return (
                    <div
                      key={date}
                      className={[
                        'vq-cal__agenda-day',
                        isPast ? 'is-past' : '',
                        isToday ? 'is-today' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <div className="vq-cal__agenda-day-stub">
                        <div className="vq-cal__agenda-day-num">{d.day}</div>
                        <div className="vq-cal__agenda-day-dow">
                          {new Date(d.year, d.month - 1, d.day).toLocaleDateString(locale, {
                            weekday: 'short',
                          })}
                        </div>
                        {isToday && (
                          <Tag tone="accent" size="sm" className="vq-cal__agenda-day-pip">
                            {messages.today}
                          </Tag>
                        )}
                      </div>
                      <ol className="vq-cal__agenda-day-list">
                        {list.map((e) => (
                          <li key={eventInstanceKey(e)} className="vq-cal__agenda-day-item">
                            <EventCard
                              event={e}
                              kindTheme={kindTheme}
                              groups={groups}
                              onClick={onSelectEvent}
                              onSelectLink={onSelectLink}
                              viewerTz={viewerTz}
                              extras={renderEventExtras?.(e)}
                              selected={selectedEventKey === eventInstanceKey(e)}
                              contextMenu={contextMenu?.(e)}
                            />
                          </li>
                        ))}
                      </ol>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <aside className="vq-cal__rail" aria-label={messages.agendaAriaLabel}>
        {nextUp !== undefined && (
          <RailCard eyebrow={messages.agendaNextUp}>
            <div className="vq-cal__rail-relative">
              {relativeDayLabelLocalized(
                parseISODate(localDateKey(nextUp.startsAt, viewerTz)),
                todayDate,
                messages,
                locale,
              )}
            </div>
            <div className="vq-cal__rail-headline">{nextUp.title}</div>
            <div className="vq-cal__rail-sub">
              {formatDayShort(parseISODate(localDateKey(nextUp.startsAt, viewerTz)), locale)}
              {(() => {
                const t = localTime(nextUp.startsAt, viewerTz);
                return t !== null ? ` · ${t}` : '';
              })()}
            </div>
          </RailCard>
        )}
        <RailCard eyebrow={messages.agendaThisView}>
          <div className="vq-cal__agenda-stat-grid">
            <div className="vq-cal__agenda-stat">
              <div className="vq-cal__agenda-stat-num">{upcomingCount}</div>
              <div className="vq-cal__agenda-stat-lbl">{messages.agendaUpcomingStat}</div>
            </div>
            <div className="vq-cal__agenda-stat">
              <div className="vq-cal__agenda-stat-num is-mute">{pastCount}</div>
              <div className="vq-cal__agenda-stat-lbl">{messages.agendaPastStat}</div>
            </div>
            <div className="vq-cal__agenda-stat">
              <div className="vq-cal__agenda-stat-num">{weeks.length}</div>
              <div className="vq-cal__agenda-stat-lbl">{messages.agendaWeeksStat}</div>
            </div>
          </div>
        </RailCard>
        <RailCard eyebrow={messages.agendaByKind}>
          {Object.entries(kindTheme)
            .filter(([k]) => k !== 'default')
            .map(([k, def]) => {
              const n = byKind.get(k) ?? 0;
              const pct = sorted.length > 0 ? (n / sorted.length) * 100 : 0;
              return (
                <div key={k} className="vq-cal__agenda-kind">
                  <span className="vq-cal__agenda-kind-label">
                    <span
                      className="vq-cal__agenda-kind-dot"
                      style={{ background: def.color }}
                      aria-hidden="true"
                    />
                    {def.label}
                  </span>
                  <span className="vq-cal__agenda-kind-bar">
                    <span
                      className="vq-cal__agenda-kind-fill"
                      style={{ width: `${pct}%`, background: def.color }}
                    />
                  </span>
                  <span className="vq-cal__agenda-kind-count">{n}</span>
                </div>
              );
            })}
        </RailCard>
        {weeks.length > 1 && (
          <RailCard eyebrow={messages.agendaJumpToWeek}>
            <List size="sm" aria-label={messages.agendaJumpToWeek}>
              {weeks.map((wk) => {
                const total = wk.days.reduce((n, d) => n + d.list.length, 0);
                return (
                  <List.Item
                    key={wk.key}
                    title={weekRangeLabel(wk.start, wk.end, locale)}
                    trailing={
                      <Badge tone="neutral" size="sm" numeric>
                        {total}
                      </Badge>
                    }
                    onClick={() =>
                      weekRefs.current.get(wk.key)?.scrollIntoView({
                        behavior: reducedMotion ? 'auto' : 'smooth',
                        block: 'start',
                      })
                    }
                  />
                );
              })}
            </List>
          </RailCard>
        )}
      </aside>
    </div>
  );
}
